import {Injectable} from "@angular/core";
import {CoreApiService} from "./core-api.service";
import {
  ApiResponseDto,
  DtoChangePassword,
  DtoLoginRequest,
  DtoLoginResponse,
  DtoLoginResponseFailed,
  DtoUpdateEmail,
  DtoUpdatePhone,
  EuOtpCodeLoginType,
  ExLoginPassCodeRequestDto, ExOtpLoginInitiateRequest,
  ExOtpLoginInitiateResponse, ExOtpLoginVerifyRequest, ExOtpLoginVerifyResponse,
  TokenRefreshRequestDto
} from "../interfaces/core.types";
import {Observable, throwError} from "rxjs";
import {catchError, tap} from "rxjs/operators";
import {error} from "@angular/compiler-cli/src/transformers/util";

@Injectable({providedIn: 'root'})
export abstract class AuthService {

  private readonly tokenStorageAuthToken = 'authToken';
  private readonly tokenStorageAuthTokenType = 'authTokenType';
  private readonly tokenStorageRefreshToken = 'refreshToken';

  private refreshTokenBusy: boolean = false;
  private lastTokenRefresh?: Date;

  constructor(protected coreApiService: CoreApiService) {
  }

  private storeToken(token: DtoLoginResponse): void {
    this.lastTokenRefresh = new Date();
    localStorage.setItem(this.tokenStorageAuthToken, token.accessToken);
    localStorage.setItem(this.tokenStorageAuthTokenType, token.tokenType);
    localStorage.setItem(this.tokenStorageRefreshToken, token.refreshToken);
  }

  public setToken(token: DtoLoginResponse): void {
    this.storeToken(token);
  }

  public getToken(): DtoLoginResponse | null {
    let authToken = localStorage.getItem(this.tokenStorageAuthToken);
    let authTokenType = localStorage.getItem(this.tokenStorageAuthTokenType);
    let refreshToken = localStorage.getItem(this.tokenStorageRefreshToken);

    if (authToken && authTokenType && refreshToken) {

      // check whether we should refresh the token
      if (this.lastTokenRefresh) {
        if (this.refreshTokenLimitPassed(this.lastTokenRefresh)) {
          // refresh the token
          this.refreshToken();
        }
      } else {
        this.lastTokenRefresh = new Date();
      }

      return {
        tokenType: authTokenType,
        accessToken: authToken,
        refreshToken: refreshToken
      }
    } else {
      return null;
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenStorageAuthToken);
    localStorage.removeItem(this.tokenStorageAuthTokenType);
    localStorage.removeItem(this.tokenStorageRefreshToken);

  }

  refreshTokenLimitPassed(lastTokenRefresh: Date): boolean {
    const currentTime: Date = new Date();
    const sixtySecondsLater: Date = new Date(lastTokenRefresh.getTime() + 1000); // 60000 milliseconds = 60 seconds

    return currentTime >= sixtySecondsLater;
  }

  public isAuthenticated(): boolean {

    if (this.getToken()) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Login.
   * @param userName
   * @param password
   */
  public login(userName: string, password: string): Observable<DtoLoginResponse | DtoLoginResponseFailed> {

    const dto: DtoLoginRequest = {
      email: userName,
      password: password,
      deviceInfo: this.coreApiService.getDeviceInfo()
    };

    return this.coreApiService.post<DtoLoginResponse>(dto, "mvseu/api/auth/login").pipe(
      tap(response => {

        if ('accessToken' in response && 'refreshToken' in response) {
          this.storeToken(response);
        }

      })
    );

  }


  /**
   * Login using pass code.
   * @param passCode The pass code string.
   */
  public loginPassCode(passCode: string): Observable<DtoLoginResponse | DtoLoginResponseFailed> {

    const dto: ExLoginPassCodeRequestDto = {
      passCode: passCode,
      deviceInfo: this.coreApiService.getDeviceInfo()
    };

    return this.coreApiService.post<DtoLoginResponse>(dto, "mvseu/api/auth/loginPassCode").pipe(
      tap(response => {

        if ('accessToken' in response && 'refreshToken' in response) {
          this.storeToken(response);
        }

      })
    );
  }

  /**
   * Initiates OTP login by sending loginType and referenceContact.
   * @param loginType Type of OTP login (EMAIL or MOBILE_PHONE)
   * @param referenceContact The contact to which OTP should be sent
   */
  public otpLoginInitiate(
    loginType: EuOtpCodeLoginType,
    referenceContact: string
  ): Observable<ExOtpLoginInitiateResponse> {

    const dto: ExOtpLoginInitiateRequest = {
      loginType: loginType,
      referenceContact: referenceContact,
      deviceInfo: this.coreApiService.getDeviceInfo()
    };

    return this.coreApiService.post<ExOtpLoginInitiateResponse>(dto, 'mvseu/api/auth/otpLoginInitiate');
  }

  /**
   * Verifies the OTP code and returns a passCode if successful.
   * @param loginType Type of OTP login (EMAIL or MOBILE_PHONE)
   * @param referenceContact The contact used for OTP (email or phone)
   * @param enteredOtpCode The code entered by the user
   */
  public otpLoginVerify(
    loginType: EuOtpCodeLoginType,
    referenceContact: string,
    enteredOtpCode: string
  ): Observable<ExOtpLoginVerifyResponse> {

    const dto: ExOtpLoginVerifyRequest = {
      loginType,
      referenceContact,
      enteredOtpCode,
      deviceInfo: this.coreApiService.getDeviceInfo()
    };

    return this.coreApiService.post<ExOtpLoginVerifyResponse>(dto, 'mvseu/api/auth/otpLoginVerify');
  }


  /**
   * refresh token.
   */
  public async refreshToken() {

    if (this.refreshTokenBusy) {
      return;
    }

    this.refreshTokenBusy = true;

    let token = this.getToken();

    const refreshToken: TokenRefreshRequestDto = {
      refreshToken: token?.refreshToken
    }

    this.coreApiService.post<DtoLoginResponse>(refreshToken, "mvseu/api/auth/refresh").subscribe(
      response => {
        // execute ...
        this.storeToken(response);
        this.refreshTokenBusy = false;
      }, error => {
        // token expired
        this.refreshTokenBusy = false;
        this.lastTokenRefresh = undefined;
      }
    );

  }

  public changePassword(oldPassword: string, newPassword: string): Observable<ApiResponseDto> {
    const dto: DtoChangePassword = {
      oldPassword: oldPassword,
      newPassword: newPassword,
      deviceInfo: this.coreApiService.getDeviceInfo()
    };
    return this.coreApiService.post(dto, `mvseu/api/user/password/update`);
  }

  public updateEmail(newEmail: string, password: string): Observable<ApiResponseDto> {
    const dto: DtoUpdateEmail = {
      newEmail: newEmail,
      password: password
    };
    return this.coreApiService.post(dto, `mvseu/api/user/email/update`);
  }

  public updatePhone(phone: string, password: string): Observable<ApiResponseDto> {
    const dto: DtoUpdatePhone = {
      newPhone: phone,
      password: password
    };
    return this.coreApiService.post(dto, `mvseu/api/user/phone/update`);
  }

}
